import { clsx } from 'clsx';
import React from 'react';
import { DrawerField, DrawerSectionHeader } from '../../../components/drawer-components';
import { TooltippedText } from '../../../components/tooltipped-text';
import { EMPTY_VALUE } from './providerDisplay';

/** Label/value grid used by every drawer tab (same shape as the clearance details tab). */
export const FIELD_GRID = 'tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-3 tw:gap-y-1.5 tw:text-sm';

export type FieldSpec = {
  label: string;
  value: React.ReactNode;
  whitespacePreLine?: boolean;
  /** Label above the value, full width (for lists / long text). */
  stackLabel?: boolean;
};

type TestIdProps = { 'data-testid'?: string };

/** A titled block of read-only fields. */
export const FieldSection: React.FC<
  TestIdProps & {
    title: string;
    icon?: React.ReactNode;
    fields: FieldSpec[];
    className?: string;
  }
> = ({ title, icon, fields, className, 'data-testid': testId }) => (
  // min-w-0: grid items default to min-width:auto, so a wide value would push
  // the section across the gutter instead of wrapping.
  <section className={clsx('tw:min-w-0', className)} data-testid={testId}>
    <DrawerSectionHeader text={title} icon={icon} />
    <div className={FIELD_GRID}>
      {fields.map((field) => (
        <DrawerField
          key={field.label}
          label={field.label}
          value={field.value}
          disableIndent
          whitespacePreLine={field.whitespacePreLine}
          stackLabel={field.stackLabel}
        />
      ))}
    </div>
  </section>
);

/** One row of a repeated collection (a license, a reference, ...). */
export const RecordCard: React.FC<
  TestIdProps & {
    title: React.ReactNode;
    /** Upper-left slot, left of the title — e.g. the card's selection checkbox. */
    leading?: React.ReactNode;
    rightElement?: React.ReactNode;
    fields: FieldSpec[];
    muted?: boolean;
    /** Bottom-right slot, e.g. the document paperclip menu / a repair action. */
    footer?: React.ReactNode;
  }
> = ({ title, leading, rightElement, fields, muted, footer, 'data-testid': testId }) => (
  <div
    className={clsx(
      'tw:flex tw:flex-col tw:rounded-lg tw:border tw:border-zinc-200 tw:bg-white tw:p-3',
      muted && 'tw:opacity-60'
    )}
    data-testid={testId}
  >
    <div className="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:mb-2">
      <div className="tw:flex tw:min-w-0 tw:items-center tw:gap-2">
        {leading}
        <div className="tw:text-sm tw:font-medium tw:text-zinc-800">{title}</div>
      </div>
      {rightElement}
    </div>
    <div className={FIELD_GRID}>
      {fields.map((field) => (
        <DrawerField
          key={field.label}
          label={field.label}
          value={field.value}
          disableIndent
          whitespacePreLine={field.whitespacePreLine}
          stackLabel={field.stackLabel}
        />
      ))}
    </div>
    {footer && (
      // mt-auto pins the slot to the bottom when the grid stretches cards to equal height
      <div className="tw:mt-auto tw:pt-2 tw:flex tw:items-center tw:justify-end tw:gap-2">
        {footer}
      </div>
    )}
  </div>
);

/** A titled list of RecordCards with an empty-state line. */
export const RecordSection: React.FC<
  TestIdProps & {
    title: string;
    icon?: React.ReactNode;
    emptyText: React.ReactNode;
    count: number;
    /**
     * Right edge of the title bar: where another item is added once the list has
     * some ("Add one"). An empty list offers it inside `emptyText` instead.
     */
    action?: React.ReactNode;
    /** Left edge of the title bar, before the title — e.g. the section's select-all checkbox. */
    leading?: React.ReactNode;
    children?: React.ReactNode;
  }
> = ({ title, icon, emptyText, count, action, leading, children, 'data-testid': testId }) => (
  <section data-testid={testId}>
    {/* Light brand band so a new collection is obvious while scrolling a long tab;
        same type treatment as DrawerSectionHeader. */}
    <div className="tw:mb-2 tw:flex tw:items-center tw:gap-2 tw:rounded-md tw:bg-brand-green-3 tw:px-2 tw:py-1">
      {leading}
      {icon}
      <span className="tw:text-brand-green-0 tw:italic tw:text-sm">{title}</span>
      {action && <span className="tw:ml-auto tw:flex tw:items-center tw:text-sm">{action}</span>}
    </div>
    {count === 0 ? (
      // Same footprint as a RecordCard so an empty collection reads as an entry,
      // not a hole in the two-column layout.
      <div className="tw:flex tw:min-h-32 tw:items-center tw:justify-center tw:rounded-lg tw:border tw:border-dashed tw:border-zinc-300 tw:bg-white tw:p-3 tw:text-sm tw:text-zinc-500">
        {emptyText}
      </div>
    ) : (
      <div className="tw:grid tw:grid-cols-1 tw:gap-2 tw:@xl:grid-cols-2">{children}</div>
    )}
  </section>
);

/**
 * The "add what is missing" bar: a lead-in ("Nothing on file yet — add:") and one
 * shortcut per thing that has nothing on file, instead of an empty section for each.
 */
export const AddBar: React.FC<
  TestIdProps & { lead: React.ReactNode; children: React.ReactNode }
> = ({ lead, children, 'data-testid': testId }) => (
  <div
    className="tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:rounded-md tw:border tw:border-dashed tw:border-zinc-300 tw:bg-white tw:px-3 tw:py-2"
    data-testid={testId}
  >
    <span className="tw:text-sm tw:text-zinc-600">{lead}</span>
    {children}
  </div>
);

/**
 * Codes rendered as-is with the CodeDefinition title on hover — for
 * categories whose titles are too long to print inline (board certifications).
 */
export const TooltippedCodes: React.FC<{
  codes: readonly string[];
  map: Record<string, string> | undefined;
}> = ({ codes, map }) => {
  if (codes.length === 0) return <>{EMPTY_VALUE}</>;
  return (
    <span className="tw:inline-flex tw:flex-wrap tw:gap-x-1">
      {codes.map((code, index) => (
        <span key={code} className="tw:whitespace-nowrap">
          <TooltippedText tooltipContent={map?.[code] || code}>
            <span className="tw:cursor-help tw:underline tw:decoration-dotted tw:decoration-zinc-400 tw:underline-offset-2">
              {code}
            </span>
          </TooltippedText>
          {index < codes.length - 1 && ','}
        </span>
      ))}
    </span>
  );
};

/**
 * The v2 edit affordance for a card: a small muted pencil beside the thing it edits
 * (title or line), green on hover — never a coloured button in the footer. Used by the
 * references and credential cards and the IMLC line (see also ShiftsTab).
 */
export const EditPencil: React.FC<
  TestIdProps & { onClick: () => void; title: string; 'aria-label'?: string }
> = ({ onClick, title, 'aria-label': ariaLabel, 'data-testid': testId }) => (
  <button
    type="button"
    className="tw:text-zinc-400 tw:hover:text-brand-green-0 tw:cursor-pointer tw:bg-transparent tw:border-0 tw:p-0"
    onClick={onClick}
    title={title}
    aria-label={ariaLabel ?? title}
    data-testid={testId}
  >
    <i className="fas fa-pencil tw:text-xs" aria-hidden="true" />
  </button>
);

/** Paperclip that says whether a document row has an upload — presence only, never a download. */
export const FilePresenceIcon: React.FC<{ hasFile: boolean }> = ({ hasFile }) => (
  <TooltippedText tooltipContent={hasFile ? 'File on file' : 'No file uploaded'}>
    <i
      className={clsx(
        'fa-solid fa-fw fa-paperclip',
        hasFile ? 'tw:text-brand-green-0' : 'tw:text-zinc-300'
      )}
      aria-label={hasFile ? 'File on file' : 'No file uploaded'}
      data-testid={hasFile ? 'file-present' : 'file-missing'}
    />
  </TooltippedText>
);
